/**
 * Map GraphQL / Apollo mutation errors to user-facing copy.
 *
 * Payment errors from submitCheckout carry a user-facing `message` and
 * `extensions.paymentError` / `extensions.code` (CARD_DECLINED, CARD_3DS_REQUIRED).
 * Prefer the server message for those — it already includes specific decline
 * reasons (insufficient funds, expired card, etc.).
 */
export function prettyErrorMessage(mutationError: any): string {
  const gqlError = mutationError?.graphQLErrors?.[0]
  const extensions = gqlError?.extensions || {}
  const code: string | undefined =
    extensions.code || gqlError?.code || mutationError?.networkError?.result?.errors?.[0]?.extensions?.code
  const serverMessage: string | undefined =
    (typeof gqlError?.message === 'string' && gqlError.message) ||
    (typeof mutationError?.networkError?.result?.errors?.[0]?.message === 'string'
      ? mutationError.networkError.result.errors[0].message
      : undefined)

  // Payment errors: always prefer the (already user-friendly) server message
  if (extensions.paymentError && serverMessage && serverMessage !== 'CARD_DECLINED') {
    return serverMessage
  }

  // Server sent a known error key as the message (legacy throw Error(errors.X.key))
  if (serverMessage && errors[serverMessage]) {
    return errors[serverMessage]
  }

  // Known code with a static map — but do not clobber a more specific server message
  if (code && errors[code]) {
    if (
      serverMessage &&
      serverMessage !== code &&
      serverMessage !== errors[code] &&
      !/^(Error|ApolloError|GraphQL error)/i.test(serverMessage)
    ) {
      return serverMessage
    }
    return errors[code]
  }

  if (serverMessage) {
    return stripApolloPrefix(serverMessage)
  }

  if (mutationError?.message) {
    return stripApolloPrefix(String(mutationError.message))
  }

  if (mutationError?.toString) {
    return stripApolloPrefix(mutationError.toString())
  }

  return 'An unexpected error occurred. Please try again.'
}

function stripApolloPrefix(message: string): string {
  return message
    .replace(/^ApolloError:\s*/i, '')
    .replace(/^GraphQL error:\s*/i, '')
    .replace(/^Error:\s*/i, '')
}

export const errors: Record<string, string> = {
  ACTIVE_SUBSCRIPTION_REQUIRED: 'An active subscription is required.',
  APP_ALREADY_ACTIVATED: 'Selected App already activated.',
  APP_ACCOUNT_ALREADY_CLAIMED: 'That account has already been claimed by another user.',
  APP_NOT_FOUND: 'Selected App not found.',
  COMMENT_EDITING_CUTOFF_REACHED: "A comment cannot be edited if it's older than 30 minutes.",
  COURSE_NOT_FOUND: 'Selected course not found.',
  COURSE_ENROLLMENT_ALREADY_EXISTS: 'Already enrolled in this course.',
  EMAIL_IN_USE: 'Another account is already using that email address.',
  EMAIL_ALREADY_CONFIRMED: 'User email already confirmed.',
  EMAIL_OR_PHONE_REQUIRED: 'An Email or Phone number is required.',
  EMAIL_PASSWORD_REQUIRED: 'Email password required.',
  EMAIL_REQUIRED: 'Email address required.',
  PURCHASE_FAILED_MPA: 'Your payment failed to process.',
  NOT_FOUND: 'Record Not Found',
  CANCELLATION_DISCOUNT_ALREADY_APPLIED: 'This 50% offer has already been applied to this subscription.',
  SUBSCRIPTION_NOT_CANCELLABLE:
    'This subscription cannot be changed or canceled online. Please contact support@startups.com.',
  PASSWORD_ALREADY_SET: 'Password already set.',
  PASSWORD_INVALID: 'Invalid password.',
  PASSWORD_NOT_SET:
    'A password is not set. We have emailed you a login link, or you can try resetting your password.',
  SIGNUP_PASSWORD_INVALID:
    'The submitted password does not match one previously set. Please try logging in or resetting your password.',
  LIMIT_EXCEEDED: 'You have exceeded the limit for this action.',
  PHONE_ALREADY_CONFIRMED: 'Phone number already confirmed.',
  PHONE_INVALID: 'The phone number with the given country code appears to be invalid.',
  PHONE_REQUIRED: 'A phone number is required.',
  PRODUCT_ALREADY_PURCHASED: 'Product already purchased.',
  PRODUCT_NOT_FOUND: 'Product not found.',
  USER_ALREADY_LOGGED_IN: 'User already logged in.',
  USER_NOT_AUTHENTICATED: 'User not authenticated.',
  USER_NOT_AUTHORIZED: 'User not authorized.',
  USER_NOT_AUTHORIZED_TO_ACTIVATE_APPLICATION:
    'Current user is not authorized to activate selected application.',
  USER_NOT_FOUND: 'The user was not found.',
  USER_NOT_FOUND_WITH_EMAIL: 'User not found with that email address.',
  USER_NOT_FOUND_WITH_EMAIL_CONFIRMATION_TOKEN: 'Cannot find user with email confirmation token.',
  USER_NOT_FOUND_WITH_PASSWORD_RESET_TOKEN: 'Password reset token is expired or invalid.',
  USER_NOT_FOUND_WITH_PHONE: 'Cannot find user with phone number.',
  USER_NOT_FOUND_WITH_PHONE_CONFIRMATION_TOKEN: 'Cannot find user with phone confirmation token.',
  USER_WITH_EMAIL_EXISTS: 'This email address is already registered. Please try logging in.',
  USER_WITH_PHONE_EXISTS: 'This phone number is already registered. Please try logging in.',
  // Generic fallback when the server did not attach a more specific decline reason
  CARD_DECLINED: 'We were unable to process your payment. Please try another card or contact your bank.',
  CARD_3DS_REQUIRED: 'Your card requires 3D Secure authorization for payment. Please try again.',
  ADVISOR_SESSION_ALREADY_ACTIVE:
    'You already have an open session with this advisor — a request waiting on a time, a confirmed booking, or a live call. Finish or cancel it in Sessions before booking another.',
  ADVISOR_SESSION_EXPERT_UNAVAILABLE: 'This advisor is not accepting paid sessions right now.',
  ADVISOR_SESSION_INPUT_INVALID: 'Check the session length, topic, and suggested times, then try again.',
  ADVISOR_SESSION_PRECHARGE_FAILED:
    'We could not authorize payment for this session. Check your card and try again.',
  ADVISOR_SESSION_PRECHARGE_REQUIRED: 'Payment must be authorized before this session can continue.',
  ADVISOR_SESSION_NOT_JOINABLE: 'This session cannot be joined until the schedule is confirmed.',
  ADVISOR_SESSION_INVALID_TRANSITION: 'That action is not available for this session right now.',
  ADVISOR_PAYOUT_CONNECT_COUNTRY_REQUIRED:
    'Choose the country where you will receive payouts before connecting Stripe.',
  ADVISOR_PAYOUT_CONNECT_COUNTRY_UNSUPPORTED: 'Stripe Connect payouts are not available in that country yet.',
  ADVISOR_PAYOUT_CONNECT_RESET_HAS_BALANCE:
    'This Stripe account still has a payout in progress or a balance at Stripe. Wait until it is paid out before removing the account.',
  ADVISOR_PAYOUT_CONNECT_DASHBOARD_UNAVAILABLE:
    'Finish Stripe setup with Update account before opening your Express dashboard.',
}
